/**
 * NASDAQ Quantum — adapter de SOLO LECTURA sobre el motor de presentación.
 *
 * CERO lógica de cálculo propia: traduce la config Quantum a la config del
 * motor (solo sesiones/tz cruzan la frontera -- WP §1: es el único grupo
 * editable), llama a `detect()` del motor y mapea su salida al shape interno
 * (ids namespaced, `direction` en mayúsculas por convención de la
 * plataforma). Nunca lanza excepción: cualquier entrada inválida devuelve la
 * salida vacía.
 */

import { detect as engineDetect, defaultConfig as engineDefaultConfig } from './engine.js'

function cleanTime(t) {
  if (t == null || t === '' || Number.isNaN(t)) return null
  return t > 1e10 ? Math.floor(t / 1000) : t
}

function emptyOutput() {
  return { ranges: [], signals: [], activeSignal: null, lastSignal: null, dashboard: null, emaSeries: [] }
}

/** Solo las sesiones/tz cruzan la frontera -- el resto son constantes internas fijas del motor. */
function toEngineConfig(cfg) {
  return {
    ...engineDefaultConfig,
    sessA: cfg.sessA,
    sessB: cfg.sessB,
    sessC: cfg.sessC,
    tz: cfg.tz,
  }
}

function mapSignal(sig, context) {
  if (!sig || (sig.dir !== 'long' && sig.dir !== 'short')) return null
  if (!Number.isFinite(sig.entry) || !Number.isFinite(sig.sl) || !Number.isFinite(sig.tp)) return null
  const time = cleanTime(sig.entryTime)
  if (time == null) return null

  const symbol = typeof context?.symbol === 'string' && context.symbol ? context.symbol : 'na'
  const timeframe = typeof context?.timeframe === 'string' && context.timeframe ? context.timeframe : 'na'

  return {
    id: `nq:${symbol}:${timeframe}:${time}`,
    day: sig.day ?? null,
    time,
    direction: sig.dir === 'long' ? 'LONG' : 'SHORT',
    entry: sig.entry,
    sl: sig.sl,
    tp: sig.tp,
    rangeHigh: Number.isFinite(sig.rangeHigh) ? sig.rangeHigh : null,
    rangeLow: Number.isFinite(sig.rangeLow) ? sig.rangeLow : null,
    status: sig.status ?? 'closed',
    closeReason: sig.closeReason ?? null,
    closeTime: cleanTime(sig.closeTime),
    closePx: Number.isFinite(sig.closePx) ? sig.closePx : null,
  }
}

function mapRange(r) {
  if (!r) return null
  const startTime = cleanTime(r.startTime)
  const endTime = cleanTime(r.endTime)
  if (startTime == null) return null
  return {
    date: r.date ?? null,
    startTime,
    endTime: endTime ?? startTime,
    rangeHigh: Number.isFinite(r.rangeHigh) ? r.rangeHigh : null,
    rangeLow: Number.isFinite(r.rangeLow) ? r.rangeLow : null,
  }
}

/**
 * Ejecuta el motor sobre las velas y mapea su salida al shape Quantum.
 * @param {Array} candles  velas ya saneadas (OHLC numéricos), TODAS cerradas
 * @param {Object} cfg     config ya resuelta por resolveConfig()
 * @param {{symbol?: string|null, timeframe?: string|null}} context
 */
export function runAdapter(candles, cfg, context) {
  try {
    if (!Array.isArray(candles) || candles.length === 0 || !cfg) return emptyOutput()
    const result = engineDetect(candles, toEngineConfig(cfg))
    if (!result || typeof result !== 'object') return emptyOutput()

    const ranges = (Array.isArray(result.ranges) ? result.ranges : []).map(mapRange).filter(Boolean)
    const signals = (Array.isArray(result.signals) ? result.signals : [])
      .map(s => mapSignal(s, context))
      .filter(Boolean)

    const activeSignal = result.activeTrade ? mapSignal(result.activeTrade, context) : null
    const lastSignal = result.lastSignal ? mapSignal(result.lastSignal, context) : null
    const dashboard = result.dashboard && typeof result.dashboard === 'object' ? result.dashboard : null
    const emaSeries = Array.isArray(result.emaSeries)
      ? result.emaSeries.filter(p => p && Number.isFinite(p.value)).map(p => ({ time: cleanTime(p.time), value: p.value }))
      : []

    return { ranges, signals, activeSignal, lastSignal, dashboard, emaSeries }
  } catch {
    return emptyOutput()
  }
}
